import { RepeatIcon, XCircleIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { useRetryVisitorParkingPayment } from '@/api/visitor-parking/retry-visitor-parking-payment'
import { Button } from '@/components/business/visitor-parking/button'
import { ErrorMessage } from '@/components/business/visitor-parking/error-message'
import { HelpSection } from '@/components/business/visitor-parking/help-section'
import { Icon } from '@/components/ui/icon'
import { useVisitorParkingStore } from '@/pages/visitor-parking/visitor-parking.store'
import { VisitorParkingFormLayout } from '@/pages/visitor-parking/visitor-parking-form-layout'

import * as S from './payment-error.styled'

interface PaymentErrorProps {
  onRetryPayment: () => void
}

export const PaymentError = ({ onRetryPayment }: PaymentErrorProps) => {
  const { t } = useTranslation(['visitor-parking'])
  const { duration, paymentSession, setPaymentSession } = useVisitorParkingStore()
  const {
    isPending,
    mutateAsync: retryVisitorParkingPayment,
    isError,
    error,
  } = useRetryVisitorParkingPayment()

  const handleRetryPayment = async () => {
    if (!paymentSession) return

    const session = await retryVisitorParkingPayment({
      duration: duration,
      sessionId: paymentSession.sessionId,
      visitorId: paymentSession.visitorId,
    })
    setPaymentSession({
      amount: session.amount,
      clientSecret: session.clientSecret,
      sessionId: session.sessionId,
      ticketNumber: session.ticketNumber,
      visitorId: session.visitorId,
    })
    onRetryPayment()
  }

  return (
    <VisitorParkingFormLayout
      footer={
        <Button loading={isPending} onClick={handleRetryPayment}>
          {t('visitor-parking:paymentError.retry')} <Icon icon={RepeatIcon} />
        </Button>
      }
    >
      <S.Title>{t('visitor-parking:paymentError.title')}</S.Title>
      <S.Card>
        <S.Icon icon={XCircleIcon} size="8rem" weight="fill" />
        <S.ErrorInfo>{t('visitor-parking:paymentError.errorInfo')}</S.ErrorInfo>
        <S.ErrorDescription>
          {t('visitor-parking:paymentError.errorDescription')}
        </S.ErrorDescription>
        {isError && (
          <ErrorMessage
            message={error?.message || t('visitor-parking:paymentError.genericError')}
          />
        )}
      </S.Card>
      <HelpSection />
    </VisitorParkingFormLayout>
  )
}
